import React from 'react';
import { Dialog } from '@headlessui/react';
import { useClub } from '../context/ClubContext';
import { getCourtDisplayNameById } from '../utils/courtDisplay';
import { formatSportDisplayName } from '../utils/sportName';

const BookingSummaryModal = ({ isOpen, onClose, summary }) => {
  const { getCategoryStyle, courtData } = useClub();
  
  if (!summary) return null;

  const { successfulBookings, failedBookings, recurringRule } = summary;
  const summaryBookings = [...(successfulBookings || []), ...(failedBookings || [])];
  const primaryCourtId = summaryBookings[0]?.court || successfulBookings[0]?.court;
  const primarySportKey = primaryCourtId ? String(primaryCourtId).split('-')[0] : '';
  const sportDisplayName = primarySportKey ? formatSportDisplayName(primarySportKey) : '';
  const hasAnyDisplayNameInSummary = summaryBookings.some((booking) => {
    const court = (courtData || []).find((item) => item?.court === booking?.court);
    return String(court?.displayName || '').trim().length > 0;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T12:00:00Z').toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getBlockBookingLabel = () => {
    if (!recurringRule) return 'Repeats indefinitely';
    const frequency = recurringRule.frequency?.toLowerCase();
    if (frequency === 'weekly') {
      const day = recurringRule.day || '';
      return day ? `Every ${day}` : 'Weekly (Never Ending)';
    }
    if (frequency === 'daily') {
      return 'Every day';
    }
    return `Recurring ${frequency || ''}`;
  };

  const getDisplayDate = (booking) => {
    if (!booking?.date || booking.date === 'BLOCK') {
      return getBlockBookingLabel();
    }
    return formatDate(booking.date);
  };

  const getDisplayCourtLabel = (booking) => getCourtDisplayNameById(booking?.court, courtData, 'Court');

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-slate-600 hover:text-slate-600 rounded-full p-1 hover:bg-slate-100 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <Dialog.Title className="text-xl font-semibold text-slate-800 mb-6">
            {sportDisplayName ? `${sportDisplayName} Booking Summary` : 'Booking Summary'}
          </Dialog.Title>
          
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              {recurringRule ? (
                <span className={`inline-block border ${getCategoryStyle({ 
                  category: 'Recurring',
                  court: successfulBookings[0]?.court || 'tennis-1'
                })} px-3 py-1.5 rounded-lg text-sm font-medium`}>
                  Recurring {recurringRule.frequency}
                </span>
              ) : (
                <span className={`inline-block border ${getCategoryStyle({ 
                  category: 'Recurring',
                  court: successfulBookings[0]?.court || 'tennis-1'
                })} px-3 py-1.5 rounded-lg text-sm font-medium`}>
                  {hasAnyDisplayNameInSummary ? 'Multi-resource booking' : 'Multi-court booking'}
                </span>
              )}
              <span className="text-sm text-slate-600">
                ({successfulBookings.length} of {successfulBookings.length + failedBookings.length} bookings created)
              </span>
            </div>

            {successfulBookings.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-green-800 mb-2">Successfully Booked</h3>
                <div className="bg-green-50 rounded-lg p-4 space-y-3">
                  {successfulBookings.map((booking, index) => (
                    <div key={index} className="text-xs md:text-sm lg:text-md flex items-center justify-between">
                      <div className="text-green-800">
                        <span className="font-medium">{getDisplayCourtLabel(booking)}</span>
                        <span className="mx-2">•</span>
                        {getDisplayDate(booking)}
                      </div>
                      <div className="text-green-700">
                        {booking.startTime} - {booking.endTime}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {failedBookings.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-red-800 mb-2">Failed to Book</h3>
                <div className="bg-red-50 rounded-lg p-4 space-y-3">
                  {failedBookings.map((booking, index) => (
                    <div key={index} className="text-xs md:text-sm lg:text-md flex items-center justify-between">
                      <div className="text-red-800">
                        <span className="font-medium">{getDisplayCourtLabel(booking)}</span>
                        <span className="mx-2">•</span>
                        {getDisplayDate(booking)}
                      </div>
                      <div className="text-red-700">
                        {booking.startTime} - {booking.endTime}
                      </div>
                    </div>
                  ))}
                  <p className="text-sm text-red-700 mt-2">
                    These slots are no longer available. They may have been booked by another user.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default BookingSummaryModal; 